
import { supabase } from '@/lib/supabase'
import { Organization, OrganizationMember } from './types'

export const createOrganization = async (userId: string, name: string, plan: string = 'standard') => {
  if (!userId) {
    throw new Error('Utilisateur non connecté')
  }

  try {
    // Call RPC function to create organization
    const { data: orgId, error: rpcError } = await supabase.rpc(
      'manually_create_organization',
      { 
        p_user_id: userId,
        org_name: name,
        plan_name: plan
      }
    )
    
    if (rpcError) {
      console.error('Erreur RPC lors de la création de l\'organisation:', rpcError)
      
      // Fallback: use direct insertion if RPC fails
      const { data, error } = await supabase
        .from('organizations')
        .insert([
          { 
            name, 
            owner_id: userId, 
            plan,
            max_users: plan === 'standard' ? 3 : plan === 'professional' ? 5 : 10,
            max_profiles: plan === 'standard' ? 10 : plan === 'professional' ? 20 : 30
          }
        ])
        .select()
        .single()

      if (error) throw error

      // Add user as organization owner
      if (data) {
        const { error: memberError } = await supabase
          .from('organization_members')
          .insert([
            { organization_id: data.id, user_id: userId, role: 'owner' }
          ])

        if (memberError) throw memberError
      }
      
      return { data, error: null }
    }
    
    // If RPC was successful, return organization ID
    return { 
      data: { id: orgId }, 
      error: null 
    }
  } catch (error) {
    console.error('Erreur lors de la création de l\'organisation:', error)
    return { data: null, error }
  }
}

export const getCurrentOrganization = async (userId: string): Promise<Organization | null> => {
  if (!userId) return null

  try {
    // Get organizations the user is a member of
    const { data, error } = await supabase
      .from('organization_members')
      .select('organization_id')
      .eq('user_id', userId)
      .limit(1)

    if (error) throw error
    if (!data || data.length === 0) return null

    // Get organization details
    const { data: orgData, error: orgError } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', data[0].organization_id)
      .single()

    if (orgError) throw orgError
    return orgData
  } catch (error) {
    console.error('Erreur lors de la récupération de l\'organisation:', error)
    return null
  }
}

export const getOrganizationMembers = async (organizationId: string): Promise<OrganizationMember[]> => {
  try {
    const { data, error } = await supabase
      .from('organization_members')
      .select(`
        *,
        user:user_id (
          email,
          raw_user_meta_data
        )
      `)
      .eq('organization_id', organizationId)

    if (error) throw error

    return data.map(member => ({
      ...member,
      user: {
        email: member.user.email,
        full_name: member.user.raw_user_meta_data?.full_name
      }
    }))
  } catch (error) {
    console.error('Erreur lors de la récupération des membres:', error)
    return []
  }
}

export const inviteToOrganization = async (userId: string, organizationId: string, email: string, role: string = 'member') => {
  if (!userId) {
    throw new Error('Utilisateur non connecté')
  }

  try {
    // Check if user has permission to invite
    const { data: memberData, error: memberError } = await supabase
      .from('organization_members')
      .select('role')
      .eq('organization_id', organizationId)
      .eq('user_id', userId)
      .single()

    if (memberError) throw memberError
    if (!memberData || !['owner', 'admin'].includes(memberData.role)) {
      throw new Error('Vous n\'avez pas les droits pour inviter des membres')
    }

    // Check if organization has reached its user limit
    const { data: orgData, error: orgError } = await supabase
      .from('organizations')
      .select('current_users, max_users')
      .eq('id', organizationId)
      .single()

    if (orgError) throw orgError
    if (orgData.current_users >= orgData.max_users) {
      throw new Error('Limite d\'utilisateurs atteinte pour cette organisation')
    }

    // Create invitation
    const { data, error } = await supabase
      .from('organization_invitations')
      .insert([
        { 
          organization_id: organizationId, 
          email, 
          role,
          invited_by: userId
        }
      ])
      .select()

    if (error) throw error

    return { data, error: null }
  } catch (error) {
    console.error('Erreur lors de l\'invitation:', error)
    return { data: null, error }
  }
}
